import React, { useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Pagination,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NewsCard from '../components/NewsCard';
import { newsApi } from '../services/api';

const News: React.FC = () => {
  const { isManager } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const limit = 12;

  const { data, isLoading, error } = useQuery({
    queryKey: ['news', page, search],
    queryFn: () => newsApi.getNewsPosts({
      search: search || undefined,
      skip: (page - 1) * limit,
      limit,
    }),
  });

  const totalPages = data ? Math.ceil(data.total / limit) : 0;

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1">
            News & Announcements
          </Typography>
          {isManager && (
            <Button
              variant="contained"
              startIcon={<ManageSearchIcon />}
              onClick={() => navigate('/news/manage')}
            >
              Manage News
            </Button>
          )}
        </Box>

        {/* Search Bar */}
        <Box sx={{ mb: 4 }}>
          <TextField
            fullWidth
            placeholder="Search news..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1); // Reset to first page on search
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load news. Please try again later.
          </Alert>
        )}

        {/* No Results */}
        {!isLoading && !error && data && data.posts.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              {search ? 'No news posts found matching your search.' : 'No news posts available yet.'}
            </Typography>
          </Box>
        )}

        {/* News Grid */}
        {!isLoading && !error && data && data.posts.length > 0 && (
          <>
            <Grid container spacing={3}>
              {data.posts.map((post) => (
                <Grid item xs={12} sm={6} md={4} key={post.id}>
                  <NewsCard post={post} />
                </Grid>
              ))}
            </Grid>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  size="large"
                />
              </Box>
            )}
          </>
        )}
        </>
      </Box>
    </Container>
  );
};

export default News;
